// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  TextArea,
  Dropdown,
  Select,
  Button,
  StatusBanner,
  AppIcon,
  FormRow,
  useToast,
} from '@loa/ui-components';
import type { PolicyDetailDTO, PolicySignatoryDTO } from '../types/policyList';
import type { PolicySignatoryOverrideCreateDTO } from '../types/policyOverride';
import type { Gender, MaritalStatus } from '../types/client';
import type { PolicyUpdateDTO, PolicyOwnershipType } from '../types/policy';
import { createOverride, updateOverride, deleteOverride } from '../api/policyOverrideApi';
import { policyApi } from '../api/policyApi';
import { brokerageConfigApi, type BrokerageProvider } from '../api/brokerageConfigApi';
import { advisorApi } from '../api/advisorApi';
import { usePolicyTypeOptions } from '../hooks/usePolicyTypeOptions';
import './CombinedPolicyEditModal.css';

type CombinedPolicyEditModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  policy: PolicyDetailDTO;
  signatory: PolicySignatoryDTO;
  profileId: string;
}>;

interface OverrideFormState {
  overrideFirstName: string;
  overrideLastName: string;
  overrideEmail: string;
  overrideAddressLine1: string;
  overrideAddressLine2: string;
  overrideAddressLine3: string;
  overrideTownCity: string;
  overrideStateProvinceCounty: string;
  overridePostalCode: string;
  overrideCountryCode: string;
  overrideCountry: string;
  overrideGender: string;
  overrideMaritalStatus: string;
}

const GENDER_OPTIONS = [
  { value: 'MALE', text: 'Male' },
  { value: 'FEMALE', text: 'Female' },
];

const MARITAL_STATUS_OPTIONS = [
  { value: 'SINGLE', text: 'Single' },
  { value: 'DIVORCED', text: 'Divorced' },
  { value: 'WIDOWED', text: 'Widowed' },
  { value: 'MARRIED', text: 'Married' },
  { value: 'CIVIL_PARTNERSHIP', text: 'Civil Partnership' },
  { value: 'SEPARATED', text: 'Separated' },
];

interface PolicyFormState {
  providerId: string;
  category: string;
  policyNumber: string;
  ownershipType: PolicyOwnershipType;
  inAgency: boolean;
  loaInPlace: boolean;
}

const INITIAL_OVERRIDE: OverrideFormState = {
  overrideFirstName: '',
  overrideLastName: '',
  overrideEmail: '',
  overrideAddressLine1: '',
  overrideAddressLine2: '',
  overrideAddressLine3: '',
  overrideTownCity: '',
  overrideStateProvinceCounty: '',
  overridePostalCode: '',
  overrideCountryCode: '',
  overrideCountry: '',
  overrideGender: '',
  overrideMaritalStatus: '',
};

const ownershipTypeOptions = [
  { text: 'Solo - Single owner', value: 'SOLO' },
  { text: 'Joint - Multiple owners', value: 'JOINT' },
  { text: 'Group - Group ownership', value: 'GROUP' },
];

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function CombinedPolicyEditModal({
  open,
  onClose,
  onSuccess,
  policy,
  signatory,
  profileId,
}: CombinedPolicyEditModalProps) {
  const { showToast } = useToast();
  const isEditMode = !!signatory.override;

  // Override form state
  const [overrideForm, setOverrideForm] = useState<OverrideFormState>({ ...INITIAL_OVERRIDE });
  const [emailError, setEmailError] = useState<string | null>(null);

  // Policy form state
  const [policyForm, setPolicyForm] = useState<PolicyFormState>({
    providerId: '',
    category: '',
    policyNumber: '',
    ownershipType: 'SOLO',
    inAgency: false,
    loaInPlace: false,
  });

  // Provider loading
  const [providers, setProviders] = useState<BrokerageProvider[]>([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [brokerageId, setBrokerageId] = useState<string>('');

  // Dynamic policy type options
  const { categoryOptions, loading: loadingPolicyTypes } = usePolicyTypeOptions(brokerageId, policyForm.providerId);

  // Shared state
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  // Initialize forms when modal opens
  useEffect(() => {
    if (!open) return;

    // Override form
    if (signatory.override) {
      setOverrideForm({
        overrideFirstName: signatory.override.overrideFirstName || '',
        overrideLastName: signatory.override.overrideLastName || '',
        overrideEmail: signatory.override.overrideEmail || '',
        overrideAddressLine1: signatory.override.overrideAddressLine1 || '',
        overrideAddressLine2: signatory.override.overrideAddressLine2 || '',
        overrideAddressLine3: signatory.override.overrideAddressLine3 || '',
        overrideTownCity: signatory.override.overrideTownCity || '',
        overrideStateProvinceCounty: signatory.override.overrideStateProvinceCounty || '',
        overridePostalCode: signatory.override.overridePostalCode || '',
        overrideCountryCode: signatory.override.overrideCountryCode || '',
        overrideCountry: signatory.override.overrideCountry || '',
        overrideGender: signatory.override.overrideGender || '',
        overrideMaritalStatus: signatory.override.overrideMaritalStatus || '',
      });
      setReason(signatory.override.reason || '');
    } else {
      setOverrideForm({ ...INITIAL_OVERRIDE });
      setReason('');
    }

    // Policy form
    setPolicyForm({
      providerId: policy.providerId || '',
      category: policy.category || '',
      policyNumber: policy.policyNumber || '',
      ownershipType: (policy.ownershipType as PolicyOwnershipType) || 'SOLO',
      inAgency: policy.inAgency || false,
      loaInPlace: policy.loaInPlace || false,
    });

    setError(null);
    setEmailError(null);
    setSubmitting(false);
    setShowRemoveConfirm(false);
  }, [open, signatory, policy]);

  // Load providers
  useEffect(() => {
    if (!open) return;

    const loadProviders = async () => {
      try {
        setLoadingConfig(true);
        const advisorProfile = await advisorApi.getProfile();
        const brokId = advisorProfile.brokerageId.toString();
        setBrokerageId(brokId);
        const providersResponse = await brokerageConfigApi.getEnabledProviders(brokId);
        setProviders(providersResponse.content);
      } catch (err) {
        console.error('Failed to load brokerage configuration:', err);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadProviders();
  }, [open]);

  // Override field helpers
  const handleOverrideChange = (field: keyof OverrideFormState, value: string) => {
    setOverrideForm(prev => ({ ...prev, [field]: value }));
    setError(null);
    if (field === 'overrideEmail') {
      setEmailError(null);
    }
  };

  const handleEmailBlur = () => {
    const email = overrideForm.overrideEmail.trim();
    if (email && !isValidEmail(email)) {
      setEmailError('Please enter a valid email address.');
    } else {
      setEmailError(null);
    }
  };

  // Policy field helpers
  const handlePolicyChange = (field: keyof PolicyFormState, value: string) => {
    setPolicyForm(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  // Computed state
  const hasAnyOverrideField = Object.values(overrideForm).some(v => v.trim() !== '');

  const trimmedReason = reason.trim();
  const reasonRequired = hasAnyOverrideField;
  const reasonValid = !reasonRequired || (trimmedReason.length >= 5 && trimmedReason.length <= 1000);

  const hasInvalidEmail =
    !!overrideForm.overrideEmail.trim() && !isValidEmail(overrideForm.overrideEmail.trim());

  const hasPolicyChanges =
    policyForm.policyNumber !== (policy.policyNumber || '') ||
    policyForm.category !== (policy.category || '') ||
    policyForm.ownershipType !== ((policy.ownershipType as PolicyOwnershipType) || 'SOLO') ||
    policyForm.inAgency !== (policy.inAgency || false) ||
    policyForm.loaInPlace !== (policy.loaInPlace || false);

  const isSubmitDisabled =
    submitting ||
    loadingConfig ||
    !reasonValid ||
    hasInvalidEmail ||
    (!hasAnyOverrideField && !hasPolicyChanges);

  // Build override request
  const buildOverrideRequest = (): PolicySignatoryOverrideCreateDTO => ({
    overrideFirstName: overrideForm.overrideFirstName.trim() || null,
    overrideLastName: overrideForm.overrideLastName.trim() || null,
    overrideEmail: overrideForm.overrideEmail.trim() || null,
    overrideAddressLine1: overrideForm.overrideAddressLine1.trim() || null,
    overrideAddressLine2: overrideForm.overrideAddressLine2.trim() || null,
    overrideAddressLine3: overrideForm.overrideAddressLine3.trim() || null,
    overrideTownCity: overrideForm.overrideTownCity.trim() || null,
    overrideStateProvinceCounty: overrideForm.overrideStateProvinceCounty.trim() || null,
    overridePostalCode: overrideForm.overridePostalCode.trim() || null,
    overrideCountryCode: overrideForm.overrideCountryCode.trim() || null,
    overrideCountry: overrideForm.overrideCountry.trim() || null,
    overrideGender: (overrideForm.overrideGender || null) as Gender | null,
    overrideMaritalStatus: (overrideForm.overrideMaritalStatus || null) as MaritalStatus | null,
    reason: trimmedReason,
  });

  // Submit handler
  const handleSubmit = async () => {
    if (isSubmitDisabled) return;

    setSubmitting(true);
    setError(null);

    try {
      // 1. Handle override
      if (hasAnyOverrideField) {
        const overrideRequest = buildOverrideRequest();
        if (isEditMode) {
          await updateOverride(profileId, policy.id, signatory.id, overrideRequest);
        } else {
          await createOverride(profileId, policy.id, signatory.id, overrideRequest);
        }
      }

      // 2. Handle policy update
      if (hasPolicyChanges) {
        const policyUpdate: PolicyUpdateDTO = {
          policyNumber: policyForm.policyNumber.trim() || undefined,
          category: policyForm.category || undefined,
          providerId: policyForm.providerId,
          ownershipType: policyForm.ownershipType,
          inAgency: policyForm.inAgency,
          loaInPlace: policyForm.loaInPlace,
        };
        await policyApi.updatePolicy(profileId, policy.id, policyUpdate);
      }

      showToast('Policy updated successfully.', 'success');
      onSuccess();
      onClose();
    } catch (err) {
      const axiosErr = err as { response?: { status?: number; data?: { message?: string } } };
      if (axiosErr.response?.status === 400) {
        setError(axiosErr.response.data?.message || 'Invalid input. Please check your fields and try again.');
      } else if (axiosErr.response?.status === 403) {
        setError('You do not have permission to update this policy.');
      } else if (axiosErr.response?.status === 404) {
        setError('Policy or signatory not found. Please refresh and try again.');
      } else if (axiosErr.response?.status === 409) {
        setError('An override already exists for this signatory. Please refresh to view the existing override.');
      } else {
        setError('Failed to save changes. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Remove override handler
  const handleRemoveOverride = async () => {
    setSubmitting(true);
    setError(null);
    setShowRemoveConfirm(false);

    try {
      await deleteOverride(profileId, policy.id, signatory.id);
      showToast('Override removed successfully.', 'success');
      onSuccess();
      onClose();
    } catch (err) {
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 404) {
        setError('Override not found. It may have already been removed. Please refresh.');
      } else {
        setError('Failed to remove override. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const policyLabel = policy.policyNumber
    || (policy.category ? policy.category.charAt(0) + policy.category.slice(1).toLowerCase() : '');

  const providerOptions = providers.map(p => ({
    text: p.provider.name,
    value: p.providerId,
  }));

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        size="large"
        className="combined-policy-edit-modal"
      >
        <Modal.Header
          icon="edit"
          title={policyLabel ? `Edit policy — ${policyLabel}` : 'Edit policy'}
          onClose={handleClose}
        />
        <Modal.Content>
          {error && (
            <StatusBanner variant="error" position="inline" visible message={error} />
          )}

          {/* Section 1: Personal details for this policy */}
          <h4 className="combined-policy-edit-modal__section-header">
            Personal details for this policy
          </h4>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', opacity: loadingConfig ? 0.6 : 1 }}>
            <FormRow>
              <TextInput
                label="First name"
                placeholder={`${signatory.firstName} (current)`}
                value={overrideForm.overrideFirstName}
                onChange={(e) => handleOverrideChange('overrideFirstName', e.target.value)}
                disabled={submitting}
                maxLength={100}
                aria-label="Override first name"
                fluid
              />
              <TextInput
                label="Last name"
                placeholder={`${signatory.lastName} (current)`}
                value={overrideForm.overrideLastName}
                onChange={(e) => handleOverrideChange('overrideLastName', e.target.value)}
                disabled={submitting}
                maxLength={100}
                aria-label="Override last name"
                fluid
              />
            </FormRow>

            <FormRow>
              <TextInput
                label="Email address"
                type="email"
                placeholder={`${signatory.email} (current)`}
                value={overrideForm.overrideEmail}
                onChange={(e) => handleOverrideChange('overrideEmail', e.target.value)}
                onBlur={handleEmailBlur}
                disabled={submitting}
                maxLength={255}
                aria-label="Override email address"
                error={emailError || undefined}
                fluid
              />
              <TextInput
                label="Address line 1"
                placeholder="Address line 1"
                value={overrideForm.overrideAddressLine1}
                onChange={(e) => handleOverrideChange('overrideAddressLine1', e.target.value)}
                disabled={submitting}
                maxLength={255}
                aria-label="Override address line 1"
                fluid
              />
            </FormRow>

            <FormRow>
              <TextInput
                label="Address line 2"
                placeholder="Address line 2"
                value={overrideForm.overrideAddressLine2}
                onChange={(e) => handleOverrideChange('overrideAddressLine2', e.target.value)}
                disabled={submitting}
                maxLength={255}
                aria-label="Override address line 2"
                fluid
              />
              <TextInput
                label="Address line 3"
                placeholder="Address line 3 (optional)"
                value={overrideForm.overrideAddressLine3}
                onChange={(e) => handleOverrideChange('overrideAddressLine3', e.target.value)}
                disabled={submitting}
                maxLength={255}
                aria-label="Override address line 3"
                fluid
              />
            </FormRow>

            <FormRow>
              <TextInput
                label="City"
                placeholder="City"
                value={overrideForm.overrideTownCity}
                onChange={(e) => handleOverrideChange('overrideTownCity', e.target.value)}
                disabled={submitting}
                maxLength={100}
                aria-label="Override city"
                fluid
              />
              <TextInput
                label="Postcode"
                placeholder="Postcode (optional)"
                value={overrideForm.overridePostalCode}
                onChange={(e) => handleOverrideChange('overridePostalCode', e.target.value)}
                disabled={submitting}
                maxLength={20}
                aria-label="Override postcode"
                fluid
              />
            </FormRow>

            <FormRow>
              <TextInput
                label="State / Province"
                placeholder="State / Province"
                value={overrideForm.overrideStateProvinceCounty}
                onChange={(e) => handleOverrideChange('overrideStateProvinceCounty', e.target.value)}
                disabled={submitting}
                maxLength={100}
                aria-label="Override state or province"
                fluid
              />
              <TextInput
                label="Country Code"
                placeholder="Country code"
                value={overrideForm.overrideCountryCode}
                onChange={(e) => handleOverrideChange('overrideCountryCode', e.target.value)}
                disabled={submitting}
                maxLength={3}
                aria-label="Override country code"
                fluid
              />
            </FormRow>

            <FormRow>
              <TextInput
                label="Country"
                placeholder="Country"
                value={overrideForm.overrideCountry}
                onChange={(e) => handleOverrideChange('overrideCountry', e.target.value)}
                disabled={submitting}
                maxLength={100}
                aria-label="Override country"
                fluid
              />
              <div /> {/* Empty spacer for half-width */}
            </FormRow>

            <FormRow>
              <Dropdown
                label="Gender"
                placeholder="Select gender"
                options={GENDER_OPTIONS}
                value={overrideForm.overrideGender}
                onChange={(v) => handleOverrideChange('overrideGender', v)}
                disabled={submitting}
                clearable
                fluid
                aria-label="Override gender"
                data-testid="override-gender-dropdown"
              />
              <Dropdown
                label="Marital Status"
                placeholder="Select marital status"
                options={MARITAL_STATUS_OPTIONS}
                value={overrideForm.overrideMaritalStatus}
                onChange={(v) => handleOverrideChange('overrideMaritalStatus', v)}
                disabled={submitting}
                clearable
                fluid
                aria-label="Override marital status"
                data-testid="override-marital-status-dropdown"
              />
            </FormRow>
          </div>

          {/* Section 2: Policy details */}
          <hr className="combined-policy-edit-modal__divider" />
          <h4 className="combined-policy-edit-modal__section-header">
            Policy details
          </h4>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', opacity: loadingConfig ? 0.6 : 1, pointerEvents: loadingConfig ? 'none' : 'auto' }}>
            <FormRow>
              <Dropdown
                label="Policy provider / Institution"
                placeholder="Select a provider"
                options={providerOptions}
                value={policyForm.providerId}
                onChange={(value) => handlePolicyChange('providerId', value)}
                search
                fluid
                required
                disabled
              />
              <Dropdown
                label="Policy type"
                placeholder="Select a category"
                options={categoryOptions}
                value={policyForm.category}
                onChange={(value) => handlePolicyChange('category', value)}
                clearable
                fluid
                disabled={submitting || loadingConfig || loadingPolicyTypes}
              />
            </FormRow>

            <FormRow>
              <TextInput
                label="Policy number / Plan Reference No / Member Reference No"
                placeholder="POL-2024-001"
                value={policyForm.policyNumber}
                onChange={(e) => handlePolicyChange('policyNumber', e.target.value)}
                disabled={submitting}
                fluid
                iconRight={<AppIcon name="info circle" />}
              />
              <Select
                label="Ownership type"
                options={ownershipTypeOptions}
                value={policyForm.ownershipType}
                onChange={(e) => handlePolicyChange('ownershipType', e.target.value)}
                fluid
                required
                disabled={submitting || loadingConfig}
              />
            </FormRow>

            <FormRow>
              <Select
                label="Agency"
                options={[
                  { text: 'Not in Agency', value: 'false' },
                  { text: 'In Agency', value: 'true' },
                ]}
                value={policyForm.inAgency ? 'true' : 'false'}
                onChange={(e) => setPolicyForm(prev => ({ ...prev, inAgency: e.target.value === 'true' }))}
                fluid
                disabled={submitting || loadingConfig}
              />
              <Select
                label="LOA"
                options={[
                  { text: 'Not in Place', value: 'false' },
                  { text: 'LOA in Place', value: 'true' },
                ]}
                value={policyForm.loaInPlace ? 'true' : 'false'}
                onChange={(e) => setPolicyForm(prev => ({ ...prev, loaInPlace: e.target.value === 'true' }))}
                fluid
                disabled={submitting || loadingConfig}
                data-testid="policy-loa-in-place-select"
              />
            </FormRow>
          </div>

          {/* Section 3: Reason for this change */}
          <hr className="combined-policy-edit-modal__divider" />

          <TextArea
            label={reasonRequired ? 'Reason for this change (required, 5-1000 characters)' : 'Reason for this change'}
            placeholder="Please give a reason for this change"
            value={reason}
            onChange={(e) => {
              if (e.target.value.length <= 1000) {
                setReason(e.target.value);
                setError(null);
              }
            }}
            disabled={submitting}
            required={reasonRequired}
            rows={2}
            fluid
          />
          {reasonRequired && trimmedReason.length > 0 && trimmedReason.length < 5 && (
            <div style={{ textAlign: 'right', fontSize: '0.85em', color: '#db2828', marginTop: '0.3em' }}>
              Reason must be at least 5 characters
            </div>
          )}
        </Modal.Content>

        <Modal.Actions>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', gap: '0.5em' }}>
              {isEditMode && (
                <Button
                  variant="outlined"
                  colour="red"
                  onClick={() => setShowRemoveConfirm(true)}
                  disabled={submitting}
                  iconLeft={<AppIcon name="trash" size={16} />}
                >
                  Remove Override
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5em' }}>
              <Button variant="outlined" onClick={handleClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                variant="filled"
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
                loading={submitting}
              >
                Edit Policy
              </Button>
            </div>
          </div>
        </Modal.Actions>
      </Modal>

      {/* Remove override confirmation */}
      <Modal open={showRemoveConfirm} onClose={() => setShowRemoveConfirm(false)} size="small" variant="warning">
        <Modal.Header title="Remove Detail Override" onClose={() => setShowRemoveConfirm(false)} />
        <Modal.Content>
          <p>
            Are you sure you want to remove the detail override for {signatory.firstName} {signatory.lastName} on policy {policyLabel || 'this policy'}? Future LOAs will use the client&apos;s current profile details.
          </p>
        </Modal.Content>
        <Modal.Actions
          secondaryAction={{ label: 'Cancel', onClick: () => setShowRemoveConfirm(false) }}
          primaryAction={{ label: 'Remove Override', onClick: () => { void handleRemoveOverride(); }, colour: 'red', iconLeft: 'trash' }}
        />
      </Modal>
    </>
  );
}
